import {
  Button,
  ButtonGroup,
  Divider,
  Step,
  StepLabel,
  Stepper,
  TableHead,
  Table,
  TableRow,
  TablePagination,
  Typography,
  TableCell,
  Checkbox,
  TableBody,
  Skeleton,
  Fab,
  IconButton,
  Link as MuiLink,
  Snackbar,
  MenuItem,
  Menu,
  Drawer,
  TextField,
  Alert,
  AlertTitle,
  TableContainer,
} from '@mui/material'
import { useNavigate, Link as RouterLink } from 'react-router-dom'
import { quoteLineType } from '../types'
import './EditLines.css'
import { Add, ExpandLess, ExpandMore, MoreVert } from '@mui/icons-material'
import { LineEdit } from './LineEdit'
import { LinesNewLine } from './LinesNewLine'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import ActionBar from '../Misc/ActionBar'
import { Lines } from './Lines'
import { formatCurrency } from '../helper'
import dayjs from 'dayjs'

const COLUMN_WIDTHS = {
  CHECKBOX: '125px',
  MFR_PART_NUMBER: '150px',
  DESCRIPTION: '350px',
  DISTRIBUTOR: '130px',
  MANUFACTURER: '110px',
  UNIT_COST: '120px',
  EXT_COST: '130px',
  QTY: '80px',
  MARGIN: '135px',
  EXT_PRICE: '130px',
  CONTRACT_PRICE: '130px',
  CONTRACT_CATALOG: '130px',
  COMPLIANCE: '105px',
  ACTIONS: '65px',
}

function EditLines() {
  const navigate = useNavigate()
  const {
    quote,
    lineItems,
    setLineItems,
    lineItemsLoading,
    setLineItemsLoading,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    selectedLineItems,
    setSelectedLineItems,
    drawerState,
    setDrawerState,
    cloneSnackbar,
    setCloneSnackbar,
    deleteSnackbar,
    setDeleteSnackbar,
    bulkEditSnackbar,
    setBulkEditSnackbar,
    bulkDeleteSnackbar,
    setBulkDeleteSnackbar,
    addLineSnackbar,
    setAddLineSnackbar,
    canDrag,
    setCanDrag,
    bundleSnackbar,
    setBundleSnackbar,
    unbundleSnackbar,
    setUnbundleSnackbar,
    collapsedBundles,
    setCollapsedBundles,
    lineCount,
    quoteId,
    sortedLineItems,
    handleReloadLineItems,
    handleMenuAction,
    handleDrag,
    versions,
  } = Lines()

  return (
    <div className="quote-lines">
      <div className="quote-lines-action-bar">
        <ActionBar
          lineItems={lineItems}
          setLineItems={setLineItems}
          selectedLineItems={selectedLineItems}
          setSelectedLineItems={setSelectedLineItems}
          setBulkEditSnackbar={setBulkEditSnackbar}
          setBundleSnackbar={setBundleSnackbar}
          lineItemCount={lineCount}
        />
      </div>

      <DragDropContext
        onDragEnd={result => {
          handleDrag(result, lineItems, setLineItemsLoading)
        }}
      >
        <TableContainer className="quote-lines-table-container">
          <div className="quote-lines-header-info">
            <Typography variant="body2">Quote Number:</Typography>
            {lineItemsLoading ? (
              <Skeleton width={120} height={25} />
            ) : (
              <MuiLink component={RouterLink} to={`/quote/${quote.QuoteId}`} variant="body1">
                {quote.QuoteNumber}
              </MuiLink>
            )}
            <Typography variant="body2">Contract Vehicle:</Typography>
            {lineItemsLoading ? (
              <Skeleton width={120} height={25} />
            ) : (
              <Typography variant="body1">{quote.ContractVehicle}</Typography>
            )}
            <Typography variant="body2">Total Price:</Typography>
            {lineItemsLoading ? (
              <Skeleton width={120} height={25} />
            ) : (
              <Typography variant="body1">{formatCurrency(quote.TotalCost)}</Typography>
            )}
            <Typography variant="body2">Total Cost:</Typography>
            {lineItemsLoading ? (
              <Skeleton width={120} height={25} />
            ) : (
              <Typography variant="body1">{formatCurrency(quote.TotalCost)}</Typography>
            )}
            <Typography variant="body2">Total Fees:</Typography>
            {lineItemsLoading ? (
              <Skeleton width={120} height={25} />
            ) : (
              <Typography variant="body1">{formatCurrency(quote.TotalFees)}</Typography>
            )}
            <Typography variant="body2">Gross Profit:</Typography>
            {lineItemsLoading ? (
              <Skeleton width={120} height={25} />
            ) : (
              <Typography variant="body1">{formatCurrency(quote.GrossProfit)}</Typography>
            )}
          </div>
          <Divider orientation="horizontal" />
          <div className="quote-lines-header-stepper">
            <Stepper
              alternativeLabel
              activeStep={
                quote.Status === 'Obsolete'
                  ? 0
                  : quote.Status === 'Draft'
                  ? 1
                  : quote.Status === 'Approved'
                  ? 2
                  : quote.Status === 'Sent to Customer'
                  ? 3
                  : quote.Status === 'Closed Lost'
                  ? 4
                  : 5
              }
            >
              <Step>
                <StepLabel>Obsolete</StepLabel>
              </Step>
              <Step>
                <StepLabel>Draft</StepLabel>
              </Step>
              <Step>
                <StepLabel>Approved</StepLabel>
              </Step>
              <Step>
                <StepLabel>Sent to Customer</StepLabel>
              </Step>
              <Step>
                <StepLabel>Closed Lost</StepLabel>
              </Step>
              <Step>
                <StepLabel>Closed Won</StepLabel>
              </Step>
            </Stepper>
          </div>
          <div className="quote-lines-header-buttons">
            <div className="quote-lines-header-buttons-left">
              {lineItemsLoading || versions.length === 0 ? (
                <Skeleton width={200} height={40} />
              ) : (
                <TextField
                  label="Version"
                  value={quoteId}
                  onChange={e => {
                    navigate(`/quote/${e.target.value}/lines`)
                    setSelectedLineItems([])
                  }}
                  size="small"
                  select
                >
                  {versions.map(
                    (version: { Name: string | null; Version: number; CreatedDate: string; QuoteId: string }) => (
                      <MenuItem value={version.QuoteId} key={version.QuoteId}>
                        v{version.Version} {version.Name ? `- ${version.Name}` : ''} (
                        {dayjs(version.CreatedDate).format('MM/DD/YYYY hh:mm A')})
                      </MenuItem>
                    ),
                  )}
                </TextField>
              )}
              <ButtonGroup variant="outlined">
                <Button onClick={() => navigate(`/quote/${quoteId}/lines/customView`)}>Custom View</Button>
                <Button onClick={() => navigate(`/quote/${quoteId}/technicalrequest`)}>Technical Request</Button>
              </ButtonGroup>
              <Button
                variant="contained"
                onClick={async () => {
                  if (lineItems.some(lineItem => !lineItem.LineItemId)) {
                    await handleReloadLineItems()
                    setCanDrag(!canDrag)
                  } else {
                    setCanDrag(!canDrag)
                  }
                }}
              >
                {!canDrag ? 'Move Lines' : 'Lock Lines'}
              </Button>
            </div>
            <div className="quote-lines-header-buttons-right">
              <TablePagination
                count={lineCount}
                onPageChange={(_, newPage) => {
                  setPage(newPage)
                }}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                onRowsPerPageChange={e => {
                  setRowsPerPage(parseInt(e.target.value))
                  setPage(0)
                }}
                component="div"
              />
            </div>
          </div>
          <Table
            stickyHeader
            sx={{ minWidth: `${Object.values(COLUMN_WIDTHS).reduce((acc, curr) => acc + parseInt(curr), 0)}px` }}
          >
            <TableHead>
              <TableRow>
                <TableCell className="checkbox-column" width={COLUMN_WIDTHS.CHECKBOX}>
                  <div className="checkbox-column-content">
                    <Checkbox
                      checked={selectedLineItems.length === lineCount}
                      indeterminate={selectedLineItems.length > 0 && selectedLineItems.length < lineCount}
                      onChange={e => {
                        setSelectedLineItems(
                          e.target.checked
                            ? [
                                ...lineItems.flatMap(item => item.children),
                                ...lineItems.filter(item => item.LineNumber !== -1),
                              ]
                            : [],
                        )
                      }}
                    />
                    #
                    <IconButton
                      onClick={() =>
                        setCollapsedBundles(prev => {
                          const newSet = new Set(prev)
                          newSet.size === lineItems.length
                            ? newSet.clear()
                            : lineItems.forEach(item => newSet.add(item.Bundle ?? 'no-bundle'))
                          return newSet
                        })
                      }
                    >
                      {collapsedBundles.size === lineItems.length ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </div>
                </TableCell>
                <TableCell width={COLUMN_WIDTHS.MFR_PART_NUMBER}>Mfr Part #</TableCell>
                <TableCell sx={{ minWidth: COLUMN_WIDTHS.DESCRIPTION }}>Description</TableCell>
                <TableCell width={COLUMN_WIDTHS.DISTRIBUTOR}>Distributor</TableCell>
                <TableCell width={COLUMN_WIDTHS.MANUFACTURER}>Manufacturer</TableCell>
                <TableCell align="right" width={COLUMN_WIDTHS.QTY}>
                  Qty
                </TableCell>
                <TableCell align="right" width={COLUMN_WIDTHS.UNIT_COST}>
                  Unit Cost
                </TableCell>
                <TableCell align="right" width={COLUMN_WIDTHS.EXT_COST}>
                  Ext Cost
                </TableCell>
                <TableCell align="right" width={COLUMN_WIDTHS.MARGIN}>
                  Gross Profit (%)
                </TableCell>
                <TableCell align="right" width={COLUMN_WIDTHS.EXT_PRICE}>
                  Ext Price
                </TableCell>
                <TableCell align="right" width={COLUMN_WIDTHS.CONTRACT_PRICE}>
                  Contract Price
                </TableCell>
                <TableCell width={COLUMN_WIDTHS.CONTRACT_CATALOG}>Contract Catalog</TableCell>
                <TableCell width={COLUMN_WIDTHS.COMPLIANCE}>Compliance</TableCell>
                <TableCell width={COLUMN_WIDTHS.ACTIONS}></TableCell>
              </TableRow>
            </TableHead>
            {lineItemsLoading ? (
              <TableBody>
                {Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell className="checkbox-column">
                      <Skeleton width={40} height={35} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell>
                      <Skeleton width={25} height={35} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            ) : (
              <Droppable droppableId="all-items">
                {provided => {
                  const flatRenderItems: Array<{ item: quoteLineType; bundleId: string | null; isHeader: boolean }> = []
                  sortedLineItems.forEach(item => {
                    if (item.LineNumber === -1) {
                      flatRenderItems.push({
                        item: item,
                        bundleId: item.Bundle || null,
                        isHeader: true,
                      })
                      item.children.forEach(lineItem => {
                        flatRenderItems.push({ item: lineItem, bundleId: item.Bundle || null, isHeader: false })
                      })
                    } else {
                      flatRenderItems.push({ item: item, bundleId: null, isHeader: false })
                    }
                  })

                  return (
                    <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                      {flatRenderItems.map((renderItem, index) => {
                        let isLastChildInBundle = false
                        if (!renderItem.isHeader && renderItem.bundleId !== null) {
                          const fullBundle = lineItems.find(
                            item => item.Bundle === renderItem.bundleId && item.LineNumber === -1,
                          )
                          if (fullBundle) {
                            const lastChildInBundle = fullBundle.children[fullBundle.children.length - 1]
                            isLastChildInBundle = lastChildInBundle.LineItemId === renderItem.item.LineItemId
                          }
                        }

                        if (renderItem.isHeader) {
                          // Bundle header
                          const bundle = lineItems.find(item => item.LineItemId === renderItem.item.LineItemId)
                          return (
                            <Draggable
                              key={renderItem.item.LineItemId}
                              draggableId={renderItem.item.LineItemId}
                              index={index}
                              isDragDisabled={!canDrag}
                            >
                              {provided => (
                                <TableRow
                                  className="bundle-row"
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                >
                                  <TableCell className="checkbox-column">
                                    <div className="checkbox-column-content">
                                      <Checkbox
                                        checked={
                                          selectedLineItems.filter(lineItem => lineItem.Bundle === bundle?.Bundle)
                                            .length === (bundle?.children.length ?? 0)
                                        }
                                        indeterminate={
                                          selectedLineItems.filter(lineItem => lineItem.Bundle === bundle?.Bundle)
                                            .length > 0 &&
                                          selectedLineItems.filter(lineItem => lineItem.Bundle === bundle?.Bundle)
                                            .length < (bundle?.children.length ?? 0)
                                        }
                                        onChange={() => {
                                          if (
                                            selectedLineItems.filter(
                                              lineItem =>
                                                lineItem.Bundle === bundle?.Bundle || lineItem.LineNumber === -1,
                                            ).length > 0
                                          ) {
                                            setSelectedLineItems(prev =>
                                              prev.filter(
                                                lineItem =>
                                                  lineItem.Bundle !== bundle?.Bundle && lineItem.LineNumber !== -1,
                                              ),
                                            )
                                          } else {
                                            setSelectedLineItems(prev => [...prev, ...(bundle?.children ?? [])])
                                          }
                                        }}
                                      />
                                      <IconButton
                                        onClick={() =>
                                          setCollapsedBundles(prev => {
                                            const newSet = new Set(prev)
                                            newSet.has(renderItem.item.Bundle ?? 'no-bundle')
                                              ? newSet.delete(renderItem.item.Bundle ?? 'no-bundle')
                                              : newSet.add(renderItem.item.Bundle ?? 'no-bundle')
                                            return newSet
                                          })
                                        }
                                      >
                                        {collapsedBundles.has(renderItem.item.Bundle ?? 'no-bundle') ? (
                                          <ExpandLess />
                                        ) : (
                                          <ExpandMore />
                                        )}
                                      </IconButton>
                                    </div>
                                  </TableCell>
                                  <TableCell>{renderItem.item.MfrPartNumber ?? 'No Bundle'}</TableCell>
                                  <TableCell>{renderItem.item.Description}</TableCell>
                                  <TableCell></TableCell>
                                  <TableCell></TableCell>
                                  <TableCell align="right">{renderItem.item.Qty ?? '-'}</TableCell>
                                  <TableCell align="right">
                                    {renderItem.item.UnitCost
                                      ? renderItem.item.UnitCost.toLocaleString('en-US', {
                                          style: 'currency',
                                          currency: 'USD',
                                        })
                                      : '-'}
                                  </TableCell>
                                  <TableCell align="right">
                                    {renderItem.item.ExtCost
                                      ? renderItem.item.ExtCost.toLocaleString('en-US', {
                                          style: 'currency',
                                          currency: 'USD',
                                        })
                                      : '-'}
                                  </TableCell>
                                  <TableCell align="right">
                                    {renderItem.item.Margin ? `${renderItem.item.Margin.toFixed(2)}%` : '-'}
                                  </TableCell>
                                  <TableCell align="right">
                                    {renderItem.item.ExtPrice
                                      ? renderItem.item.ExtPrice.toLocaleString('en-US', {
                                          style: 'currency',
                                          currency: 'USD',
                                        })
                                      : '-'}
                                  </TableCell>
                                  <TableCell align="right">
                                    {renderItem.item.ContractCeilingPrice
                                      ? renderItem.item.ContractCeilingPrice.toLocaleString('en-US', {
                                          style: 'currency',
                                          currency: 'USD',
                                        })
                                      : '-'}
                                  </TableCell>
                                  <TableCell></TableCell>
                                  <TableCell></TableCell>
                                  <TableCell align="right">
                                    <IconButton
                                      onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                                        setDrawerState({
                                          type: 'menu',
                                          lineItem: renderItem.item,
                                          anchorEl: e.currentTarget,
                                        })
                                      }
                                    >
                                      <MoreVert />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              )}
                            </Draggable>
                          )
                        } else {
                          // Line item
                          return (
                            <Draggable
                              key={renderItem.item.LineItemId}
                              draggableId={renderItem.item.LineItemId}
                              index={index}
                              isDragDisabled={!canDrag}
                            >
                              {provided => (
                                <TableRow
                                  className={
                                    renderItem.bundleId === null
                                      ? 'no-bundle'
                                      : isLastChildInBundle
                                      ? 'child-row-last'
                                      : undefined
                                  }
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  sx={{
                                    visibility: collapsedBundles.has(renderItem.bundleId ?? 'no-bundle')
                                      ? 'hidden'
                                      : 'visible',
                                    display: collapsedBundles.has(renderItem.bundleId ?? 'no-bundle')
                                      ? 'none'
                                      : 'table-row',
                                  }}
                                >
                                  <TableCell className="checkbox-column">
                                    <div className="checkbox-column-content">
                                      <Checkbox
                                        checked={selectedLineItems.some(
                                          selected => selected.LineItemId === renderItem.item.LineItemId,
                                        )}
                                        onChange={() => {
                                          setSelectedLineItems(prev => {
                                            const isSelected = prev.some(
                                              selected => selected.LineItemId === renderItem.item.LineItemId,
                                            )
                                            return isSelected
                                              ? prev.filter(
                                                  selected => selected.LineItemId !== renderItem.item.LineItemId,
                                                )
                                              : [...prev, renderItem.item]
                                          })
                                        }}
                                      />
                                      <Typography variant="body2">
                                        {renderItem.item.LineNumber !== -1 ? renderItem.item.LineNumber : ''}
                                      </Typography>
                                    </div>
                                  </TableCell>
                                  <TableCell>{renderItem.item.MfrPartNumber ?? '-'}</TableCell>
                                  <TableCell>{renderItem.item.Description ?? '-'}</TableCell>
                                  <TableCell>{renderItem.item.Provider ?? '-'}</TableCell>
                                  <TableCell>{renderItem.item.Manufacturer ?? '-'}</TableCell>
                                  <TableCell align="right">{renderItem.item.Qty ?? '-'}</TableCell>
                                  <TableCell align="right">
                                    {renderItem.item.UnitCost
                                      ? renderItem.item.UnitCost.toLocaleString('en-US', {
                                          style: 'currency',
                                          currency: 'USD',
                                        })
                                      : '-'}
                                  </TableCell>
                                  <TableCell align="right">
                                    {renderItem.item.ExtCost
                                      ? renderItem.item.ExtCost.toLocaleString('en-US', {
                                          style: 'currency',
                                          currency: 'USD',
                                        })
                                      : '-'}
                                  </TableCell>
                                  <TableCell align="right">
                                    {renderItem.item.Margin ? `${renderItem.item.Margin.toFixed(2)}%` : '-'}
                                  </TableCell>
                                  <TableCell align="right">
                                    {renderItem.item.ExtPrice
                                      ? renderItem.item.ExtPrice.toLocaleString('en-US', {
                                          style: 'currency',
                                          currency: 'USD',
                                        })
                                      : '-'}
                                  </TableCell>
                                  <TableCell align="right">
                                    {renderItem.item.ContractCeilingPrice
                                      ? renderItem.item.ContractCeilingPrice.toLocaleString('en-US', {
                                          style: 'currency',
                                          currency: 'USD',
                                        })
                                      : '-'}
                                  </TableCell>
                                  <TableCell>{renderItem.item.ContractCatalog ?? 'TODO'}</TableCell>
                                  <TableCell>{renderItem.item.Compliance ?? 'TODO'}</TableCell>
                                  <TableCell align="right">
                                    <IconButton
                                      onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                                        setDrawerState({
                                          type: 'menu',
                                          lineItem: renderItem.item,
                                          anchorEl: e.currentTarget,
                                        })
                                      }
                                    >
                                      <MoreVert />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              )}
                            </Draggable>
                          )
                        }
                      })}
                      {provided.placeholder}
                    </TableBody>
                  )
                }}
              </Droppable>
            )}
          </Table>
          <div className="quote-lines-add-line-items">
            <Fab
              variant="extended"
              size="large"
              onClick={() =>
                setDrawerState({ type: 'new', lineNumber: lineItems.filter(item => item.LineNumber !== -1).length })
              }
            >
              Add New Line {<Add />}
            </Fab>
          </div>
        </TableContainer>
      </DragDropContext>

      <Menu
        open={drawerState.type === 'menu'}
        onClose={() => setDrawerState({ type: null })}
        anchorEl={drawerState.anchorEl}
        disableScrollLock={true}
      >
        <MenuItem onClick={() => handleMenuAction('edit')}>Edit</MenuItem>
        <MenuItem onClick={() => handleMenuAction('new')}>New Line</MenuItem>
        <MenuItem onClick={() => handleMenuAction('clone')}>Clone</MenuItem>
        {drawerState.lineItem?.Bundle && <MenuItem onClick={() => handleMenuAction('unbundle')}>Unbundle</MenuItem>}
        {drawerState.lineItem?.LineNumber !== -1 && (
          <MenuItem onClick={() => handleMenuAction('delete')}>Delete</MenuItem>
        )}
      </Menu>
      <Drawer open={drawerState.type === 'edit'} onClose={() => setDrawerState({ type: null })} anchor="right">
        <LineEdit
          type="single"
          open={drawerState.type === 'edit'}
          onClose={() => setDrawerState({ type: null })}
          selectedLineItem={drawerState.lineItem}
          fullLineItems={lineItems}
          setLineItems={setLineItems}
          quoteId={quoteId ?? ''}
        />
      </Drawer>
      <Drawer
        open={drawerState.type === 'new'}
        onClose={() => {
          setDrawerState({ type: null })
        }}
        anchor="right"
      >
        <LinesNewLine
          open={drawerState.lineNumber ?? 0}
          onClose={() => {
            setDrawerState({ type: null })
            setAddLineSnackbar('success')
          }}
          lineItems={lineItems}
          setLineItems={setLineItems}
          quoteId={quoteId ?? ''}
          bundle={drawerState.bundle ?? null}
        />
      </Drawer>
      <Alert
        severity="info"
        className="quote-lines-move-lines-alert"
        sx={{
          bottom:
            cloneSnackbar || deleteSnackbar || bulkEditSnackbar || bulkDeleteSnackbar || addLineSnackbar
              ? '90px'
              : '24px',
          opacity: canDrag ? 1 : 0,
          visibility: canDrag ? 'visible' : 'hidden',
        }}
      >
        <AlertTitle>Move Lines Enabled</AlertTitle>
        <Typography variant="body2">
          You can now move lines and bundles by dragging and dropping them. Changes will be saved automatically.
        </Typography>
      </Alert>
      <Snackbar
        autoHideDuration={5000}
        open={cloneSnackbar === 'success'}
        onClose={() => setCloneSnackbar(null)}
        message="Line cloned successfully"
      />
      <Snackbar open={cloneSnackbar === 'fail'}>
        <Alert severity="error" variant="filled" onClose={() => setCloneSnackbar(null)}>
          Failed to clone line item. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
      <Snackbar
        open={deleteSnackbar === 'success'}
        onClose={() => setDeleteSnackbar(null)}
        message="Line item deleted successfully"
      />
      <Snackbar open={deleteSnackbar === 'fail'}>
        <Alert severity="error" variant="filled" onClose={() => setDeleteSnackbar(null)}>
          Failed to delete line item. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
      <Snackbar
        autoHideDuration={5000}
        open={bulkEditSnackbar === 'success'}
        onClose={() => setBulkEditSnackbar(null)}
        message="Line items updated successfully"
      />
      <Snackbar open={bulkEditSnackbar === 'fail'}>
        <Alert severity="error" variant="filled" onClose={() => setBulkEditSnackbar(null)}>
          Failed to edit line items. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
      <Snackbar
        autoHideDuration={5000}
        open={addLineSnackbar === 'success'}
        onClose={() => setAddLineSnackbar(null)}
        message="Line added successfully"
      />
      <Snackbar
        autoHideDuration={5000}
        open={bundleSnackbar === 'success'}
        onClose={() => setBundleSnackbar(null)}
        message="Line items bundled successfully"
      />
      <Snackbar
        autoHideDuration={5000}
        open={unbundleSnackbar === 'success'}
        onClose={() => setUnbundleSnackbar(null)}
        message="Line items unbundled successfully"
      />
      <Snackbar
        autoHideDuration={5000}
        open={unbundleSnackbar === 'fail'}
        onClose={() => setUnbundleSnackbar(null)}
        message="Error unbundling line items"
      />
      <Snackbar
        autoHideDuration={5000}
        open={bulkDeleteSnackbar === 'success'}
        onClose={() => setBulkDeleteSnackbar(null)}
        message="Bundle and line items deleted successfully"
      />
      <Snackbar
        autoHideDuration={5000}
        open={bulkDeleteSnackbar === 'fail'}
        onClose={() => setBulkDeleteSnackbar(null)}
        message="Error deleting bundle and line items"
      />
      <Snackbar open={bundleSnackbar === 'fail'}>
        <Alert severity="error" variant="filled" onClose={() => setBundleSnackbar(null)}>
          Failed to bundle line items. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
    </div>
  )
}

export default EditLines
